/* Shared per-vault controls: Deposit button (+ dialog) and "Sweep %" of every deposit. */
import { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { ArrowDownToLine, Zap, X, Loader2, Settings2 } from "lucide-react";

export function DepositButton({ vaultId, vaultName, min = 20, max = 500, onDone, onMsg, className = "", compact = false }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/vaults/${vaultId}/deposit`, { amount: Number(amount) });
      setOpen(false); setAmount("");
      onMsg && onMsg(`${fmtMoney(Number(amount))} added to ${vaultName}. Balance ${fmtMoney(data.balance)}.`);
      onDone && onDone(data);
    } catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className={className || `inline-flex items-center justify-center gap-1.5 bg-forest text-white rounded-md ${compact ? "px-2.5 py-1.5 text-[11px]" : "px-4 py-2 text-sm"} font-medium hover:bg-forest-deep transition-colors`}
        data-testid={`vault-deposit-btn-${vaultId}`}>
        <ArrowDownToLine className="w-3.5 h-3.5" strokeWidth={1.75} /> Deposit
      </button>
      {open && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid={`vault-deposit-dialog-${vaultId}`}>
          <form onSubmit={go} className="bg-cream rounded-2xl border border-sand max-w-sm w-full p-6">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Deposit · {vaultName}</div>
              <button type="button" onClick={() => setOpen(false)} className="text-moss hover:text-ink" data-testid={`vault-deposit-close-${vaultId}`}><X className="w-4 h-4" /></button>
            </div>
            <input type="number" min={min} max={max} step="1" required autoFocus value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={`$${min}–$${max}`} className="mt-4 w-full bg-white border border-sand rounded-lg px-4 py-3 font-mono text-lg outline-none focus:ring-2 focus:ring-forest"
              data-testid={`vault-deposit-amount-${vaultId}`} />
            {err && <div className="text-sm text-clay mt-2" data-testid={`vault-deposit-error-${vaultId}`}>{err}</div>}
            <button type="submit" disabled={busy || !(Number(amount) > 0)} className="mt-4 w-full bg-forest text-white rounded-lg py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid={`vault-deposit-submit-${vaultId}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />} Add {amount ? fmtMoney(Number(amount)) : ""}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export function AutofillToggle({ vaultId, rule, onChanged, onMsg, compact = false }) {
  const [percent, setPercent] = useState(rule?.enabled ? rule.percent : "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (rule) setPercent(rule.enabled ? rule.percent : ""); }, [rule]);

  const save = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/vaults/${vaultId}/autofill`, { enabled, mode: "percent", percent: Number(percent) || 0 });
      setEditing(false);
      onMsg && onMsg(data.enabled ? `${data.percent}% of every deposit now sweeps into this vault.` : "Sweep turned off.");
      onChanged && onChanged(data);
    } catch (e) { onMsg && onMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const on = !!rule?.enabled;
  return (
    <div className={compact ? "text-[10px]" : "text-xs"} data-testid={`vault-autofill-${vaultId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium ${on ? "bg-gold text-white border-gold" : "bg-white text-moss border-sand"}`} data-testid={`vault-autofill-toggle-${vaultId}`}>
          <Zap className="w-3 h-3" strokeWidth={2} /> Sweep {on ? `${rule.percent}% of every deposit` : "% · off"}
        </span>
        <button type="button" onClick={() => setEditing((v) => !v)} className="text-sage hover:text-ink inline-flex items-center gap-1" data-testid={`vault-autofill-edit-${vaultId}`}>
          <Settings2 className="w-3 h-3" /> {editing ? "Close" : on ? "Change" : "Set sweep %"}
        </button>
      </div>
      {editing && (
        <form onSubmit={(e) => { e.preventDefault(); save(true); }} className="mt-2 rounded-lg border border-sand bg-white p-3 flex items-end gap-2 flex-wrap" data-testid={`vault-autofill-form-${vaultId}`}>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-sage font-semibold">Sweep % of each deposit</span>
            <input type="number" min="1" max="100" step="1" required value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="10" className="w-24 bg-white border border-sand rounded-md px-2 py-1.5 font-mono" data-testid={`vault-autofill-percent-${vaultId}`} />
          </label>
          <button type="submit" disabled={busy || !(Number(percent) > 0)} className="bg-gold text-white rounded-md px-3 py-1.5 font-medium hover:bg-gold-deep disabled:opacity-50" data-testid={`vault-autofill-save-${vaultId}`}>
            {busy ? "Saving…" : "Save"}
          </button>
          {on && <button type="button" onClick={() => save(false)} className="text-clay hover:underline" data-testid={`vault-autofill-off-${vaultId}`}>Turn off</button>}
        </form>
      )}
    </div>
  );
}

export function useAutofillRules() {
  const [rules, setRules] = useState({});
  const load = async () => {
    try { const { data } = await api.get("/vaults/autofill"); setRules(Object.fromEntries((data.rules || []).map((r) => [r.vault_id, r]))); } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  return { rules, reload: load };
}
